import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, Users, Utensils, Wine, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

const RestaurantPage = () => {
  const { t } = useTranslation();

  const features = [
    { icon: Utensils, title: "Cuisine Raffinée", desc: "Des plats traditionnels revisités avec créativité" },
    { icon: Wine, title: "Cave Sélectionnée", desc: "Une carte des vins genevois et internationaux" },
    { icon: Users, title: "Service Attentionné", desc: "Une équipe professionnelle et chaleureuse" },
    { icon: Heart, title: "Ambiance Conviviale", desc: "Un cadre élégant pour tous vos moments" },
  ];

  return (
    <div className="pt-20" data-testid="restaurant-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/ambiance/hero3.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              {t("about.subtitle")}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-2 mb-6">
              {t("nav.restaurant")}
            </h1>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl md:text-5xl text-primary mb-6">
                {t("about.title")}
              </h2>
              <div className="w-20 h-0.5 bg-gold mb-8" />

              <p className="text-muted-foreground text-lg leading-relaxed mb-6 font-body">
                {t("about.description1")}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6 font-body">
                {t("about.description2")}
              </p>

              <blockquote className="border-l-4 border-gold pl-6 italic text-primary font-accent text-xl mt-8">
                "{t("about.philosophy")}"
              </blockquote>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src="/images/plats/cocotte_de_veau.jpg"
                  alt="Cocotte de veau"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
              <div className="aspect-[3/4] overflow-hidden mt-8">
                <img
                  src="/images/plats/vol_au_vent.jpg"
                  alt="Vol-au-vent"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ARCOOP Section */}
      <section className="py-20 md:py-32 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src="/images/ambiance/hero3.png"
                  alt="Bâtiment ARCOOP"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="text-gold font-accent text-lg tracking-widest">
                Un lieu emblématique
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-primary mt-2 mb-6">
                L'ARCOOP
              </h2>
              <div className="w-20 h-0.5 bg-gold mb-8" />

              <p className="text-muted-foreground text-lg leading-relaxed mb-6 font-body">
                Depuis 1958, le bâtiment ARCOOP est un symbole de créativité et de collaboration à Carouge. 
                Cette coopérative d'artisans accueille aujourd'hui notre brasserie dans un cadre industriel réinventé.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed font-body">
                L'architecture unique du lieu, avec ses hauts plafonds et sa lumière naturelle, 
                crée une atmosphère singulière où modernité et patrimoine se côtoient harmonieusement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              Notre engagement
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-primary mt-2 mb-4">
              L'Expérience Carougeoise
            </h2>
            <div className="section-divider mx-auto" />
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-secondary rounded-full flex items-center justify-center">
                  <feature.icon className="h-8 w-8 text-gold" />
                </div>
                <h3 className="font-heading text-xl text-primary mb-3">{feature.title}</h3>
                <p className="text-muted-foreground font-body">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">
              Notre Galerie
            </h2>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              "/images/plats/choucroute.jpg",
              "/images/plats/moules.png",
              "/images/plats/roti.jpg",
              "/images/plats/dej.jpg",
              "/images/plats/gouter.png",
              "/images/plats/apero.jpeg",
            ].map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="aspect-square overflow-hidden"
              >
                <img
                  src={src}
                  alt={`Galerie ${i + 1}`}
                  className="w-full h-full object-cover img-zoom"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-primary mb-6">
              {t("reservation.subtitle")}
            </h2>
            <p className="text-muted-foreground text-lg mb-8 font-body">
              {t("reservation.description")}
            </p>
            <Link to="/reservation">
              <Button
                size="lg"
                className="bg-gold hover:bg-gold/90 text-white rounded-none px-10 py-7 text-sm tracking-wider uppercase btn-hover-expand"
                data-testid="restaurant-reserve-btn"
              >
                <Calendar className="mr-2 h-5 w-5" />
                {t("hero.cta_reserve")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RestaurantPage;
